import { z } from 'zod/v4';

// Decimal-valued fields (Ohm, Farad, Percent, plain Decimal) are serialized as
// JSON strings by the backend, not numbers. Keep them as strings on the wire
// to preserve precision; conversion to/from numeric values happens at the
// application boundary.
const DecimalStringRuntype = z.string();

export const CERAMIC_DIELECTRIC_CODES_LIST = [
    'X8R',
    'C0G',
    'NP0',
    'X7R',
    'X5R',
    'X8L',
    'Y5V',
    'X6S',
    'Z5U',
    'X7S',
    'Y5U',
    'C0H',
    'Y5R',
    'Y5P',
    'Y5T',
    'U2J',
    'Z5V',
    'Z5P',
    'K4000',
    'X6T',
    'X7T',
    'Z5F',
] as const;

export type CeramicDielectricCode = (typeof CERAMIC_DIELECTRIC_CODES_LIST)[number];
export const CeramicDielectricCodeRuntype = z.enum(CERAMIC_DIELECTRIC_CODES_LIST);

export type DielectricRequestDTO = z.infer<typeof DielectricRequestRuntype>;
export const DielectricRequestRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Ceramic'), data: CeramicDielectricCodeRuntype.nullable() }),
]);

export type TechnicalParametersRequestDTO = z.infer<typeof TechnicalParametersRequestRuntype>;
export const TechnicalParametersRequestRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Resistor'),
        resistance_ohms: DecimalStringRuntype,
        power_rating_watts: DecimalStringRuntype.nullable().default(null),
        tolerance_percent: DecimalStringRuntype.nullable().default(null),
        temperature_coefficient_ppmk: DecimalStringRuntype.nullable().default(null),
        voltage_rating_volts: DecimalStringRuntype.nullable().default(null),
    }),
    z.object({
        type: z.literal('Capacitor'),
        capacitance_farads: DecimalStringRuntype,
        dielectric: DielectricRequestRuntype.nullable().default(null),
        tolerance_percent: DecimalStringRuntype.nullable().default(null),
        voltage_rating_volts: DecimalStringRuntype.nullable().default(null),
    }),
]);

// The backend silently ignores unknown keys here, so we mirror that by not
// adding `.strict()`. Today only `category` is persisted.
export type AdditionalParametersDTO = z.infer<typeof AdditionalParametersRuntype>;
export const AdditionalParametersRuntype = z.object({
    category: z.string().nullable().default(null),
});

export type SpecificationRequestDTO = z.infer<typeof SpecificationRequestRuntype>;
export const SpecificationRequestRuntype = z.object({
    description: z.string().nullable().default(null),
    package_id: z.string().uuid().nullable().default(null),
    technical_parameters: TechnicalParametersRequestRuntype.nullable().default(null),
    additional_parameters: AdditionalParametersRuntype.default(() => ({ category: null })),
});

// Every variant except `Spn` is sent without an `id`: the underlying domain
// payload is the enclosing part_specification's `RawSpecificationId` (a
// content hash the caller cannot know in advance), so the backend fills it
// in from the part_specification's content. `Spn` keeps its `id` because
// the value is the matched SPN, not a raw_spec reference.
export type MatchingTypeRequestDTO = z.infer<typeof MatchingTypeRequestRuntype>;
export const MatchingTypeRequestRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Mpn') }),
    z.object({ type: z.literal('MpnAndManufacturer') }),
    z.object({ type: z.literal('Description') }),
    z.object({ type: z.literal('Spn'), id: z.string() }),
    z.object({ type: z.literal('ImportedComponentSpecification') }),
    z.object({ type: z.literal('Manual') }),
]);

export type MatchingReasonRequestDTO = z.infer<typeof MatchingReasonRequestRuntype>;
export const MatchingReasonRequestRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Automatic'), reason: MatchingTypeRequestRuntype }),
    z.object({ type: z.literal('Manual'), reason: MatchingTypeRequestRuntype.nullable() }),
]);

// `Description` is sent without an `id` because its domain payload is the
// enclosing part_specification's `RawSpecificationId`; the backend fills it
// in. The other variants carry the matched MPN / SPN value as their `id`.
export type SuggestionReasonRequestDTO = z.infer<typeof SuggestionReasonRequestRuntype>;
export const SuggestionReasonRequestRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Description') }),
    z.object({ type: z.literal('Mpn'), id: z.string() }),
    z.object({ type: z.literal('DescriptionMpn'), id: z.string() }),
    z.object({ type: z.literal('DescriptionSpn'), id: z.string() }),
]);

export type ApprovedPartRequestDTO = z.infer<typeof ApprovedPartRequestRuntype>;
export const ApprovedPartRequestRuntype = z.object({
    off_the_shelf_part_id: z.string().uuid(),
    matching_reason: MatchingReasonRequestRuntype,
});

export type SuggestedPartRequestDTO = z.infer<typeof SuggestedPartRequestRuntype>;
export const SuggestedPartRequestRuntype = z.object({
    off_the_shelf_part_id: z.string().uuid(),
    matching_reason: SuggestionReasonRequestRuntype,
});

export type PartSpecificationRequestDTO = z.infer<typeof PartSpecificationRequestRuntype>;
export const PartSpecificationRequestRuntype = z.object({
    mpn: z.string().nullable().default(null),
    manufacturer: z.string().nullable().default(null),
    manufacturer_id: z.string().uuid().nullable().default(null),
    description: z.string().nullable().default(null),
    package: z.string().nullable().default(null),
    part_type: z.string().nullable().default(null),
    approved_off_the_shelf_parts: z.array(ApprovedPartRequestRuntype).default(() => []),
    suggested_off_the_shelf_parts: z.array(SuggestedPartRequestRuntype).default(() => []),
});

export type InternalPartNumberRequestDTO = z.infer<typeof InternalPartNumberRequestRuntype>;
export const InternalPartNumberRequestRuntype = z.object({
    value: z.string(),
    revision: z.string().nullable().default(null),
});

export type CustomerPartNumberRequestDTO = z.infer<typeof CustomerPartNumberRequestRuntype>;
export const CustomerPartNumberRequestRuntype = z.object({
    value: z.string(),
    revision: z.string().nullable().default(null),
    customer_id: z.string().uuid(),
});

export type OffTheShelfComponentRequestDTO = z.infer<typeof OffTheShelfComponentRequestRuntype>;
export const OffTheShelfComponentRequestRuntype = z.object({
    type: z.literal('off_the_shelf'),
    internal_part_number: InternalPartNumberRequestRuntype,
    is_restricted_to_customers: z.boolean().default(false),
    is_preferred: z.boolean().default(false),
    specification: SpecificationRequestRuntype.default(() => ({
        description: null,
        package_id: null,
        technical_parameters: null,
        additional_parameters: { category: null },
    })),
    part_specifications: z.array(PartSpecificationRequestRuntype).default(() => []),
    customer_part_numbers: z.array(CustomerPartNumberRequestRuntype).default(() => []),
});

export const CUSTOM_COMPONENT_TYPES = ['PCB', 'Mechanical', 'Plastic', 'Cable', 'Packaging', 'Label', 'Other'] as const;
export type CustomComponentTypeDTO = (typeof CUSTOM_COMPONENT_TYPES)[number];
export const CustomComponentTypeRuntype = z.enum(CUSTOM_COMPONENT_TYPES);

export type CustomComponentRequestDTO = z.infer<typeof CustomComponentRequestRuntype>;
export const CustomComponentRequestRuntype = z.object({
    type: z.literal('custom'),
    internal_part_number: InternalPartNumberRequestRuntype,
    is_restricted_to_customers: z.boolean().default(false),
    is_preferred: z.boolean().default(false),
    description: z.string().nullable().default(null),
    imported_form_and_fit: z.string().uuid().nullable().default(null),
    custom_part_type: CustomComponentTypeRuntype,
    category: z.string().nullable().default(null),
    customer_part_numbers: z.array(CustomerPartNumberRequestRuntype).default(() => []),
});

export type ComponentRequestDTO = z.infer<typeof ComponentRequestRuntype>;
export const ComponentRequestRuntype = z.discriminatedUnion('type', [
    OffTheShelfComponentRequestRuntype,
    CustomComponentRequestRuntype,
]);

export type PutComponentsRequestDTO = z.infer<typeof PutComponentsRequestRuntype>;
export const PutComponentsRequestRuntype = z.object({
    components: z.array(ComponentRequestRuntype),
});

export type PutComponentsResponseDTO = z.infer<typeof PutComponentsResponseRuntype>;
export const PutComponentsResponseRuntype = z.object({
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
});

// Mirrors the backend's `MAX_BATCH_SIZE`
// (the source of truth). Requests above this size receive
// `components.batch_size_exceeded` (HTTP 400). The duplication is by
// literal: if the Rust constant changes, update this one too. We accept
// the duplication rather than expose a runtime limits endpoint because
// the value changes very rarely; the trade-off is documented here and on
// the Rust side.
export const PUT_COMPONENTS_MAX_BATCH_SIZE = 5000;
